import React, {useRef, useEffect, useState, useCallback} from 'react'
import PropTypes from 'prop-types'
import {useMotionValueEvent, useReducedMotion, useScroll} from 'framer-motion'
import {Box, Image as ChakraImage} from '@chakra-ui/react3'

/**
 * Generates an image filename based on prefix, extension, and index
 * @param {string} prefix - The prefix for the image filename (e.g., "frame-")
 * @param {string} extension - The file extension without dot (e.g., "jpg", "png")
 * @param {number} index - The frame index number (1-based)
 * @returns {string} The complete filename (e.g., "frame-1.jpg")
 * @example
 * getImageName("product-", "png", 5) // returns "product-5.png"
 */

/**
 * Maps scroll progress (0-1) to frame number (1-based indexing)
 * @param {number} scrollProgress - Scroll progress value between 0 and 1
 * @param {number} totalFrames - Total number of frames in the animation sequence
 * @returns {number} Frame number between 1 and totalFrames
 * @example
 * calculateFrameFromProgress(0, 29)    // returns 1 (first frame)
 * calculateFrameFromProgress(0.5, 29)  // returns 15 (middle frame)
 * calculateFrameFromProgress(1, 29)    // returns 29 (last frame)
 */
const calculateFrameFromProgress = (scrollProgress, totalFrames) => {
    // get how should be the in the progress for the current amount of frames
    const gap = 1 / totalFrames
    // Floor the division to get discrete frame steps, add 1 for 1-based indexing,
    const actualFrame = Math.floor(scrollProgress / gap) + 1
    // then clamp between 1 and imageCount to ensure valid frame bounds
    const newFrame = Math.max(1, Math.min(totalFrames, actualFrame))

    return newFrame
}

/**
 * ProductAnimated - Scroll-based Image Sequence Animation Component
 *
 * Creates a smooth scroll-triggered animation by displaying sequential images
 * on an HTML5 Canvas element. As the user scrolls, different frames are shown
 * to create a cinematic animation effect. Images are preloaded and cached for
 * smooth playback performance.
 *
 * ## Features:
 * - Scroll-based frame animation using Framer Motion
 * - Image preloading and caching for smooth performance
 * - SSR-compatible with CLS prevention
 * - High-DPI display support
 *
 * ## Missing
 * - Responsive canvas with aspect ratio preservation
 * - Consider lazy loading for large sequences
 * - Accessibility
 *
 * @typedef {Object} ProductAnimatedProps
 * @property {string} imagePrefix - Prefix for image filenames
 * @property {number} imageCount - Total number of frames
 * @property {string} imageExtension - File extension for images
 * @property {number} [width=600] - Canvas width in pixels
 * @property {number} [height=600] - Canvas height in pixels
 *
 * @param {ProductAnimatedProps} props - Component props
 * @returns {React.ReactElement}
 */
function ProductAnimated({
    imagePrefix,
    imageCount,
    imageExtension,
    imageWidth = 500,
    imageHeight = 550,
    alt
}) {
    const shouldReduceMotion = useReducedMotion()
    const getImageUrl = useCallback(
        (index) =>
            `${imagePrefix}${index}.${imageExtension}?sw=${imageWidth}&sh=${imageHeight}&crop=fill`,
        [imagePrefix, imageExtension, imageWidth, imageHeight]
    )
    const canvasRef = useRef(null)
    const containerRef = useRef(null)
    const imageCache = useRef(new Map())
    const [loading, setLoading] = useState(true)

    const {scrollYProgress} = useScroll({
        target: canvasRef,
        offset: ['start end', 'center center']
    })

    const drawFrame = useCallback((frameNumber) => {
        const canvas = canvasRef.current
        const container = containerRef.current
        if (!canvas || !container) return

        const cachedImage = imageCache.current.get(frameNumber)
        if (cachedImage && cachedImage.complete) {
            const context = canvas.getContext('2d')
            const rect = canvas.getBoundingClientRect()

            requestAnimationFrame(() => {
                const containerRect = container.getBoundingClientRect()
                // Set actual canvas dimensions (internal resolution)
                canvas.width = containerRect.width
                canvas.height = containerRect.height

                // Calculate scale to fit image within canvas while maintaining aspect ratio
                const imageAspect = cachedImage.width / cachedImage.height
                const canvasAspect = rect.width / rect.height

                let drawWidth, drawHeight
                if (imageAspect > canvasAspect) {
                    // Image is wider - fit to canvas width
                    drawWidth = rect.width
                    drawHeight = rect.width / imageAspect
                } else {
                    // Image is taller - fit to canvas height
                    drawHeight = rect.height
                    drawWidth = rect.height * imageAspect
                }

                // Center the image
                const x = (rect.width - drawWidth) / 2
                const y = (rect.height - drawHeight) / 2

                context.drawImage(cachedImage, x, y, drawWidth, drawHeight)
            })
        }
    }, [])

    const updateFrame = useCallback(
        (value) => {
            // Don't update frames while images are still loading
            if (loading) return

            // Draw the frame directly instead of using state
            const newFrame = calculateFrameFromProgress(value, imageCount)
            drawFrame(newFrame)
        },
        [imageCount, loading, drawFrame]
    )

    useMotionValueEvent(scrollYProgress, 'change', !shouldReduceMotion ? updateFrame : () => {})

    // preload images
    useEffect(() => {
        const cache = imageCache.current

        const loadImage = (frameNumber) =>
            new Promise((res, rej) => {
                const img = new Image()
                img.onload = () => {
                    cache.set(frameNumber, img)
                    res()
                }

                img.onerror = (err) => {
                    console.error(`Failed to load image frame ${frameNumber}`)
                    console.error(err)
                    rej()
                }

                img.src = getImageUrl(frameNumber)
            })

        const loadPromises = Array.from({length: imageCount}).map((_, index) =>
            loadImage(index + 1)
        )

        Promise.allSettled(loadPromises).then(() => {
            setLoading(false)
        })

        return () => {
            cache.clear()
        }
    }, [imageCount, imageExtension, imagePrefix, getImageUrl])

    useEffect(() => {
        if (!loading) {
            drawFrame(1)
        }
    }, [loading, drawFrame])

    return (
        <Box
            ref={containerRef}
            width="100%"
            height="100%"
            display="flex"
            justifyContent="center"
            position="relative"
        >
            <ChakraImage
                src={getImageUrl(1)}
                alt={alt}
                width="100%"
                height="100%"
                position="absolute"
                objectFit="contain"
                top={0}
                left={0}
                opacity={loading ? 1 : 0}
                aria-hidden={!loading}
                transition="opacity 0.3s ease"
            />
            <canvas
                aria-label={alt}
                role="img"
                ref={canvasRef}
                style={{
                    width: '100%',
                    height: '100%',
                    display: 'block',
                    margin: '0 auto',
                    opacity: loading ? 0 : 1
                }}
            />
        </Box>
    )
}

ProductAnimated.propTypes = {
    imagePrefix: PropTypes.string.isRequired,
    imageCount: PropTypes.number.isRequired,
    imageExtension: PropTypes.string.isRequired,
    imageWidth: PropTypes.number,
    imageHeight: PropTypes.number,
    containerHeight: PropTypes.object,
    alt: PropTypes.string.isRequired
}

// this named export allow us to test this calculation on isolation without all the
// complexities of the scroll mocking
export {calculateFrameFromProgress}
export default ProductAnimated
